import React, { useEffect, useState } from "react";
import { Card, Table, Button, Tag, Input, message } from "antd";
import {
  EyeOutlined,
  SearchOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Wallet,
  Download,
  Upload,
  ReceiptText,
} from "lucide-react";
import reapi from "../apis/reapi";
import CustomPagination from "../Components/CustomPagination";

export default function Transactions() {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);

  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const token = localStorage.getItem("token");

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      const skip = (currentPage - 1) * pageSize;

      const res = await reapi.get(
        `/transactions?skip=${skip}&limit=${pageSize}&type=${activeTab}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTransactions(res.data?.data?.data || []);
      setStats(res.data?.data?.stats || {});
      setTotalRecords(res.data?.data?.total || 0);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load transactions"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, pageSize, activeTab]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTransactions();
  };

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_, record, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      width: 160,
      render: (date) => {
        const d = new Date(date);
        return (
          <div>
            <div className="font-medium">
              {d.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
            <div className="text-slate-500 text-sm">
              {d.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 170,
      render: (type) => {
        const isCredit = type === "credit";

        return (
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                isCredit
                  ? "bg-green-100 text-green-600"
                  : "bg-orange-100 text-orange-600"
              }`}
            >
              {isCredit ? <Download size={18} /> : <Upload size={18} />}
            </div>

            <span className="font-medium">
              {isCredit ? "Credits Added" : "Credits Used"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "txnname",
      width: 260,
      render: (value, record) => (
        <div>
          <div className="font-medium">{value}</div>
          <div className="text-slate-500 text-sm">Txn ID: {record.txnid}</div>
        </div>
      ),
    },
    {
      title: "Credits",
      dataIndex: "amount",
      width: 130,
      render: (value, record) => {
        const isCredit = record.type === "credit";

        return (
          <span
            className={`font-bold ${
              isCredit ? "text-green-600" : "text-red-500"
            }`}
          >
            {isCredit ? "+" : "-"}
            {value}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 130,
      render: (value, record) =>
        record.type === "credit" ? <span>₹{value}</span> : <span>—</span>,
    },
    {
      title: "Payment ID",
      dataIndex: "refid",
      width: 190,
      render: (value, record) => (
        <span>{value || record.txnid || "—"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (status) => {
        if (status === "success") {
          return <Tag color="success">Success</Tag>;
        }

        if (status === "pending") {
          return <Tag color="warning">Pending</Tag>;
        }

        return <Tag color="error">Failed</Tag>;
      },
    },
    {
      title: "Actions",
      width: 100,
      render: (_, record) => (
        <Button
          shape="circle"
          icon={<EyeOutlined />}
          onClick={() => console.log(record)}
        />
      ),
    },
  ];

  return (
    <div className="w-full min-w-0 overflow-hidden bg-[#f8f9ff] p-4 md:p-6">
      <div className="w-full max-w-[1400px] mx-auto min-w-0">
        <div className="mb-7">
          <h1 className="text-3xl font-extrabold m-0">Transactions</h1>
          <p className="text-slate-600 mt-2">
            View all your credit purchases and usage history.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-7">
          <StatsCard
            icon={<Wallet size={32} />}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
            title="Total Transactions"
            value={stats?.totalTransactions || 0}
            subtitle="All time"
          />

          <StatsCard
            icon={<Download size={32} />}
            iconBg="bg-green-100"
            iconColor="text-green-600"
            title="Credits Added"
            value={stats?.creditsAdded || 0}
            valueColor="text-green-600"
            subtitle={`Across ${stats?.creditTxnCount || 0} transactions`}
          />

          <StatsCard
            icon={<Upload size={32} />}
            iconBg="bg-orange-100"
            iconColor="text-orange-600"
            title="Credits Used"
            value={stats?.creditsUsed || 0}
            valueColor="text-orange-600"
            subtitle={`Across ${stats?.debitTxnCount || 0} transactions`}
          />

          <StatsCard
            icon={<ReceiptText size={32} />}
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
            title="Current Balance"
            value={stats?.currentBalance || 0}
            valueColor="text-blue-600"
            subtitle="Available Credits"
          />
        </div>

        <Card className="rounded-2xl shadow-sm overflow-hidden">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 mb-5">
            <div className="flex flex-wrap gap-6 border-b xl:border-b-0 pb-2 xl:pb-0">
              <TabButton
                active={activeTab === "all"}
                onClick={() => {
                  setActiveTab("all");
                  setCurrentPage(1);
                }}
              >
                All
              </TabButton>

              <TabButton
                active={activeTab === "credit"}
                onClick={() => {
                  setActiveTab("credit");
                  setCurrentPage(1);
                }}
              >
                Credits Added
              </TabButton>

              <TabButton
                active={activeTab === "debit"}
                onClick={() => {
                  setActiveTab("debit");
                  setCurrentPage(1);
                }}
              >
                Credits Used
              </TabButton>

              <TabButton
                active={activeTab === "failed"}
                onClick={() => {
                  setActiveTab("failed");
                  setCurrentPage(1);
                }}
              >
                Payment Failed
              </TabButton>
            </div>
          </div>

          <div className="mb-5 max-w-[520px]">
            <Input
              size="large"
              placeholder="Search by transaction ID or description..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </div>

          <div className="w-full max-w-[calc(100vw-120px)] overflow-x-auto">
            <Table
              loading={loading}
              columns={columns}
              dataSource={transactions}
              rowKey="_id"
              pagination={false}
              scroll={{ x: 1300 }}
            />
          </div>
        </Card>

        <CustomPagination
          current={currentPage}
          pageSize={pageSize}
          total={totalRecords}
          onChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`pb-3 px-1 font-semibold transition border-b-2 ${
        active
          ? "text-purple-600 border-purple-600"
          : "text-slate-600 border-transparent hover:text-purple-600"
      }`}
    >
      {children}
    </button>
  );
}

function StatsCard({
  icon,
  iconBg,
  iconColor,
  title,
  value,
  valueColor = "text-slate-900",
  subtitle,
}) {
  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex items-center gap-5">
        <div
          className={`w-16 h-16 rounded-full ${iconBg} ${iconColor} flex items-center justify-center shrink-0`}
        >
          {icon}
        </div>

        <div>
          <p className="text-slate-700 m-0">{title}</p>
          <h2 className={`text-3xl font-extrabold my-2 ${valueColor}`}>
            {value}
          </h2>
          <p className="text-slate-500 m-0">{subtitle}</p>
        </div>
      </div>
    </Card>
  );
}